import { Label } from '@/components/UI/Label';
import { Switch } from '@/components/UI/Switch';
import { useLoadPlaygroundDemoDataMutation } from '@/util/hooks/mutations/usePlaygroundDemoDataMutation';
import usePlaygroundModeMutation from '@/util/hooks/mutations/usePlaygroundModeMutation';
import usePlaygroundMode from '@/util/hooks/queries/usePlaygroundMode';

/**
 * Self-service QA Playground Mode toggle for the user/settings menu (Feature #8075,
 * PBI #8080). Renders nothing while the initial status check is in flight or once it's
 * confirmed unavailable (env gate off, prod) — AC1. When the status check fails for any
 * other reason and there is no prior known state to fall back on, this shows an explicit
 * "status unknown" row instead of either the normal switch or nothing: silently hiding here
 * would read as a false "you're in normal mode" claim, which is worse than admitting we
 * don't know (PR #2577 review, Koketso Ramokhubathi). This is UX only: it never decides
 * suppression itself, only requests the on/off state via the US-2 endpoints that the
 * backend (US-3/US-4) enforces.
 *
 * #9141: turning Playground ON also seeds the demo data, so it appears straight away as in
 * the design prototype (https://claude.ai/artifact/UxudxDwSRhdUoQersrmHiv). The seed runs
 * only after the ON request succeeds: every seed endpoint 403s unless the session is already
 * active. Styling (label, helper text, switch track) follows the prototype too.
 *
 * Rendered as a plain row (not a DropdownMenuItem), the same shape as the neighbouring
 * menu-naming-toggle. Pointer events are stopped on the row so flipping the switch doesn't
 * close the menu; onPointerDown (not onClick) is used so the row itself isn't flagged as a
 * fake interactive element with no keyboard handler (SonarQube typescript:S6848, S1082) —
 * the Switch remains the sole interactive control either way.
 */
const PlaygroundModeMenuToggle = () => {
  const { isUnavailable, isUnknown, isActive, isLoading } = usePlaygroundMode();
  const { mutate: setPlaygroundMode, isPending } = usePlaygroundModeMutation();
  const { mutate: seedDemoData } = useLoadPlaygroundDemoDataMutation({
    silentOnSuccess: true
  });

  if (isLoading || isUnavailable) {
    return null;
  }

  if (isUnknown) {
    return (
      <div
        className="flex flex-col gap-0.5 px-4 py-2.5"
        onPointerDown={(e) => e.stopPropagation()}
      >
        <Label className="text-[13.5px] font-semibold text-slate-900">
          Playground Mode
        </Label>
        <span className="mt-0.5 text-[11.5px] leading-[1.4] text-amber-700">
          Couldn&apos;t confirm this session&apos;s state. Try again shortly.
        </span>
      </div>
    );
  }

  const handleCheckedChange = (checked: boolean) => {
    setPlaygroundMode(checked, {
      onSuccess: () => {
        if (checked) {
          seedDemoData();
        }
      }
    });
  };

  return (
    <div
      className="flex items-start justify-between gap-3 px-4 py-2.5"
      onPointerDown={(e) => e.stopPropagation()}
    >
      <div className="flex flex-col">
        <Label
          htmlFor="playground-mode-toggle"
          className="text-[13.5px] font-semibold text-slate-900"
        >
          Playground Mode
        </Label>
        <span className="mt-0.5 text-[11.5px] leading-[1.4] text-[#64748b]">
          {isActive
            ? 'Emails and ERP syncs are suppressed and logged for this session.'
            : 'Off — this session behaves like normal.'}
        </span>
      </div>
      <Switch
        id="playground-mode-toggle"
        checked={isActive}
        disabled={isPending}
        onCheckedChange={handleCheckedChange}
        aria-label="Toggle QA Playground Mode for this session"
        className="h-[22px] w-10 data-[state=unchecked]:bg-[#C7CBD6] [&>span]:h-[18px] [&>span]:w-[18px] [&>span]:data-[state=checked]:translate-x-[18px]"
      />
    </div>
  );
};

export default PlaygroundModeMenuToggle;
